import { useEffect, useRef } from 'react';
import type { XmiMember } from '../types';

const JCL_KEYWORDS = new Set(['CNTL', 'COMMAND', 'DD', 'ELSE', 'ENDIF', 'EXEC', 'IF', 'INCLUDE', 'JCLLIB', 'JOB', 'OUTPUT', 'PEND', 'PROC', 'SET', 'THEN', 'XMIT']);
const REXX_KEYWORDS = new Set(['ADDRESS', 'ARG', 'CALL', 'DO', 'DROP', 'ELSE', 'END', 'EXIT', 'EXPOSE', 'FOREVER', 'IF', 'INTERPRET', 'ITERATE', 'LEAVE', 'NOP', 'NUMERIC', 'OPTIONS', 'OTHERWISE', 'PARSE', 'PROCEDURE', 'PULL', 'PUSH', 'QUEUE', 'RETURN', 'SAY', 'SELECT', 'SIGNAL', 'THEN', 'TRACE', 'UPPER', 'WHEN']);
const CLIST_KEYWORDS = new Set(['PROC', 'CONTROL', 'WRITE', 'WRITENR', 'SET', 'IF', 'THEN', 'ELSE', 'DO', 'END', 'EXIT', 'ERROR', 'GOTO', 'READ', 'GETFILE', 'PUTFILE', 'OPENFILE', 'CLOSEFILE']);
const ASSEMBLER_KEYWORDS = new Set(['AIF', 'AGO', 'ANOP', 'BALR', 'BASR', 'BCTR', 'BE', 'BER', 'BH', 'BHR', 'BL', 'BLR', 'BNE', 'BNER', 'BR', 'C', 'CLC', 'CLI', 'DC', 'DS', 'DROP', 'EJECT', 'END', 'ENTRY', 'EQU', 'GET', 'L', 'LA', 'LARL', 'LM', 'LR', 'MVC', 'MVI', 'OPEN', 'PRINT', 'PUT', 'SAVE', 'STM', 'ST', 'USING', 'XR']);
const COBOL_KEYWORDS = new Set(['ACCEPT', 'ADD', 'CALL', 'CICS', 'CLOSE', 'COMPUTE', 'CONFIGURATION', 'DATA', 'DISPLAY', 'DIVISION', 'ELSE', 'END-IF', 'ENVIRONMENT', 'EVALUATE', 'EXEC', 'EXIT', 'FILE', 'GOBACK', 'IDENTIFICATION', 'IF', 'LINKAGE', 'MOVE', 'OPEN', 'PERFORM', 'PROCEDURE', 'PROGRAM-ID', 'READ', 'SECTION', 'SELECT', 'STOP', 'SUBTRACT', 'THEN', 'UNTIL', 'VARYING', 'WHEN', 'WORKING-STORAGE', 'WRITE']);

interface Token {
  kind: 'plain' | 'keyword' | 'comment' | 'string' | 'number' | 'operator' | 'label' | 'name' | 'directive';
  text: string;
}

export function MemberPreview({
  member,
  searchQuery,
  highlightLineNumbers,
  activeHighlightLineNumber
}: {
  member: XmiMember;
  searchQuery?: string;
  highlightLineNumbers?: number[];
  activeHighlightLineNumber?: number | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lines = member.text.split('\n');
  const highlightSet = new Set(highlightLineNumbers ?? []);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [member.name]);

  useEffect(() => {
    if (!searchQuery || highlightSet.size === 0) {
      return;
    }
    const targetSelector = activeHighlightLineNumber
      ? `[data-line-number="${activeHighlightLineNumber}"]`
      : '.member-preview__line--match';
    const targetLine = containerRef.current?.querySelector<HTMLElement>(targetSelector);
    targetLine?.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [activeHighlightLineNumber, highlightSet, searchQuery, member.name]);

  return (
    <div className="member-preview-shell">
      <div className="member-preview-shell__header">
        <span>{member.name}</span>
        <span>{member.language.toUpperCase()}</span>
        <span>{lines.length} lines</span>
      </div>
      <div className="member-preview" ref={containerRef}>
        <div className="member-preview__canvas">
          {lines.map((line, index) => (
            <div
              className={`member-preview__line${highlightSet.has(index + 1) ? ' member-preview__line--match' : ''}${activeHighlightLineNumber === index + 1 ? ' member-preview__line--match-active' : ''}`}
              key={`${member.name}-${index}`}
              data-line-number={index + 1}
            >
              <span className="member-preview__line-number">{String(index + 1).padStart(5, '0')}</span>
              <code className="member-preview__code">
                {tokenizeLine(member.language, line).map((token, tokenIndex) => (
                  <span key={`${index}-${tokenIndex}`} className={`token token--${token.kind}`}>
                    {token.text || ' '}
                  </span>
                ))}
              </code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function tokenizeLine(language: XmiMember['language'], line: string): Token[] {
  if (language === 'jcl') {
    return tokenizeJcl(line);
  }
  if (language === 'rexx') {
    return tokenizeRexxLike(line, REXX_KEYWORDS);
  }
  if (language === 'clist') {
    return tokenizeClist(line);
  }
  if (language === 'assembler') {
    return tokenizeAssembler(line);
  }
  if (language === 'cobol') {
    return tokenizeCobol(line);
  }
  return [{ kind: 'plain', text: line }];
}

function tokenizeJcl(line: string): Token[] {
  if (line.startsWith('//*')) {
    return [{ kind: 'comment', text: line }];
  }
  if (!line.startsWith('//')) {
    return [{ kind: 'plain', text: line }];
  }

  const match = /^(\/\/)(\S{0,8})(\s*)(\S+)?(.*)$/.exec(line);
  if (!match) {
    return [{ kind: 'plain', text: line }];
  }

  const [, prefix, name, gap, op = '', rest = ''] = match;
  return [
    { kind: 'directive', text: prefix },
    { kind: 'label', text: name },
    { kind: 'plain', text: gap },
    { kind: JCL_KEYWORDS.has(op.toUpperCase()) ? 'keyword' : 'name', text: op },
    ...tokenizeMixed(rest, JCL_KEYWORDS)
  ];
}

function tokenizeClist(line: string): Token[] {
  const trimmed = line.trimStart();
  if (trimmed.startsWith('/*')) {
    return [{ kind: 'comment', text: line }];
  }

  const tokens: Token[] = [];
  let index = 0;
  while (index < line.length) {
    const char = line[index];
    if (char === '\'' || char === '"') {
      const end = findQuotedEnd(line, index, char);
      tokens.push({ kind: 'string', text: line.slice(index, end) });
      index = end;
      continue;
    }
    if (char === '&') {
      const symbolMatch = /^&[A-Za-z0-9#$@]+/.exec(line.slice(index));
      if (symbolMatch) {
        tokens.push({ kind: 'label', text: symbolMatch[0] });
        index += symbolMatch[0].length;
        continue;
      }
    }
    if (/[=()+\-*/,<>]/.test(char)) {
      tokens.push({ kind: 'operator', text: char });
      index += 1;
      continue;
    }
    if (/[0-9]/.test(char)) {
      const numberMatch = /^[0-9]+(?:\.[0-9]+)?/.exec(line.slice(index));
      if (numberMatch) {
        tokens.push({ kind: 'number', text: numberMatch[0] });
        index += numberMatch[0].length;
        continue;
      }
    }
    const wordMatch = /^[A-Za-z@#$!?][A-Za-z0-9@#$!?]*/.exec(line.slice(index));
    if (wordMatch) {
      const word = wordMatch[0];
      tokens.push({ kind: CLIST_KEYWORDS.has(word.toUpperCase()) ? 'keyword' : 'name', text: word });
      index += word.length;
      continue;
    }
    tokens.push({ kind: 'plain', text: char });
    index += 1;
  }
  return tokens;
}

function tokenizeRexxLike(line: string, keywords: Set<string>): Token[] {
  const trimmed = line.trimStart();
  if (trimmed.startsWith('/*')) {
    return [{ kind: 'comment', text: line }];
  }
  const commentIndex = line.indexOf('--');
  if (commentIndex >= 0) {
    return [...tokenizeMixed(line.slice(0, commentIndex), keywords), { kind: 'comment', text: line.slice(commentIndex) }];
  }
  return tokenizeMixed(line, keywords);
}

function tokenizeAssembler(line: string): Token[] {
  const trimmed = line.trimStart();
  if (trimmed.startsWith('.*') || trimmed.startsWith('*')) {
    return [{ kind: 'comment', text: line }];
  }

  const match = /^(\s*)([A-Za-z#$@][A-Za-z0-9#$@]{0,7})?(\s+)?([A-Za-z][A-Za-z0-9]{0,7})?(.*)$/.exec(line);
  if (!match) {
    return [{ kind: 'plain', text: line }];
  }

  const [, indent, label = '', gap = '', op = '', rest = ''] = match;
  const tokens: Token[] = [{ kind: 'plain', text: indent }];
  if (label) {
    tokens.push({ kind: 'label', text: label });
  }
  tokens.push({ kind: 'plain', text: gap });
  if (op) {
    tokens.push({ kind: ASSEMBLER_KEYWORDS.has(op.toUpperCase()) ? 'keyword' : 'name', text: op });
  }
  tokens.push(...tokenizeMixed(rest, ASSEMBLER_KEYWORDS));
  return tokens;
}

function tokenizeCobol(line: string): Token[] {
  if (line.length > 6 && ['*', '/', 'D'].includes(line[6])) {
    return [{ kind: 'comment', text: line }];
  }
  return tokenizeMixed(line, COBOL_KEYWORDS);
}

function tokenizeMixed(segment: string, keywords: Set<string>): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  while (index < segment.length) {
    const char = segment[index];
    if (char === '\'' || char === '"') {
      const end = findQuotedEnd(segment, index, char);
      tokens.push({ kind: 'string', text: segment.slice(index, end) });
      index = end;
      continue;
    }
    if (/[=(),+\-*/<>]/.test(char)) {
      tokens.push({ kind: 'operator', text: char });
      index += 1;
      continue;
    }
    if (/[0-9]/.test(char)) {
      const numberMatch = /^[0-9]+(?:\.[0-9]+)?/.exec(segment.slice(index));
      if (numberMatch) {
        tokens.push({ kind: 'number', text: numberMatch[0] });
        index += numberMatch[0].length;
        continue;
      }
    }
    const wordMatch = /^[A-Za-z#$@][A-Za-z0-9#$@-]*/.exec(segment.slice(index));
    if (wordMatch) {
      const word = wordMatch[0];
      tokens.push({ kind: keywords.has(word.toUpperCase()) ? 'keyword' : 'name', text: word });
      index += word.length;
      continue;
    }
    tokens.push({ kind: 'plain', text: char });
    index += 1;
  }
  return tokens;
}

function findQuotedEnd(text: string, start: number, quote: string): number {
  let index = start + 1;
  while (index < text.length) {
    if (text[index] === quote) {
      if (text[index + 1] === quote) {
        index += 2;
        continue;
      }
      return index + 1;
    }
    index += 1;
  }
  return text.length;
}
